import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';

const defaultPatination = {
  pageIndex: 0,
  pageSize: 10,
};

type Pagination = {
  pageIndex: number;
  pageSize: number;
};

export type InfiniteScrollData<T = unknown> = {
  pageTotal: number;
  items: T[];
};

type InfiniteScrollFetcher<T = unknown> = (
  pagination: Pagination,
  controller: AbortController,
) => Promise<InfiniteScrollData<T>>;

export type InfiniteScrollFetchParams<T = unknown> = {
  fetcher: InfiniteScrollFetcher<T>;
  /**
   * Optional function to compare items for uniqueness.
   *
   * If provided, it will be used to determine if an item is already in the list.
   *
   * If not provided, it checks for the existence of an item with the same `id` property.
   */
  compareFn?: (a: T, b: T) => boolean;
  initialPagination?: Pagination;
};

const defaultCompareFn = <T>(a: T, b: T) => (a as any).id === (b as any).id;

/** @deprecated Use `useInfiniteScroll` instead */
export const useInfiniteScrollFetch = <T = unknown>({
  fetcher: fetcherParamFn,
  compareFn,
  initialPagination = defaultPatination,
}: InfiniteScrollFetchParams<T>) => {
  const [items, setItems] = useState<T[]>([]);
  const [pagination, setPagination] = useState(initialPagination);
  const [shouldModifyPagination, setShouldModifyPagination] = useState(true);

  const controller = useRef(new AbortController());
  const observer = useRef<IntersectionObserver | null>(null);

  const fetcher = useCallback(() => fetcherParamFn(pagination, controller.current), [fetcherParamFn, pagination]);
  const { data, isValidating } = useSWR([fetcher, pagination, controller.current], fetcher, {
    onSuccess: (data) => {
      if (data?.items?.length === 0) return;
      setShouldModifyPagination(true);
    },
  });

  const lastElementRef = useCallback(
    (node: Element | null) => {
      if (!node) return;
      if (observer.current) {
        observer.current.disconnect();
      }
      observer.current = new IntersectionObserver((entries) => {
        if (!shouldModifyPagination) return;
        if (!entries[0].isIntersecting) return;
        if (!data) return;
        setPagination((prev) => ({ ...prev, pageIndex: prev.pageIndex + 1 }));
        setShouldModifyPagination(false);
      });

      observer.current.observe(node);
    },

    [shouldModifyPagination, data],
  );

  useEffect(() => {
    if (!data) return;
    setItems((prev) => {
      const newItems: T[] = [];
      data.items.forEach((item) => {
        const isAlready = prev.find(
          (existentItem) => compareFn?.(existentItem, item) ?? defaultCompareFn(existentItem, item),
        );
        if (isAlready) return;
        newItems.push(item);
      });
      return [...prev, ...newItems];
    });
  }, [compareFn, data]);

  return {
    items,
    lastElementRef,
    isValidating,
  };
};
